import { Component, Input, OnChanges, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StatusComponent } from '../../ui/status/status.component';
import { EmptyStateComponent } from '../../ui/empty-state/empty-state.component';
import { DataTableComponent } from '../../ui/data-table/data-table.component';
import { MessagesReport } from '../../../pages/organization/campaigns/types/campaignDetails';
import { formatCount, formatWhen, toDate } from '../../../../utils/sms';

interface SendRow {
  key: string;
  referenceId: string;
  message: string;
  when: string;
  at: number;
  status: string;
  recipients: string;
  /** 0–100, or null while the send has no result yet. */
  rate: number | null;
}

/**
 * Every send of every campaign message: when it went (or goes) out, its
 * status, audience and delivery rate. A row opens that message's report.
 */
@Component({
  selector: 'app-campaign-messages-report-table',
  standalone: true,
  imports: [StatusComponent, EmptyStateComponent, DataTableComponent],
  templateUrl: './campaign-messages-report-table.component.html',
  styleUrl: './campaign-messages-report-table.component.css',
})
export class CampaignMessagesReportTableComponent implements OnChanges {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  @Input({ required: true }) messageData: any[] = [];
  @Input() loading = false;

  rows: SendRow[] = [];

  ngOnChanges() {
    const reports: MessagesReport[] = Array.isArray(this.messageData) ? this.messageData : [];
    this.rows = reports
      .filter((report) => !!report)
      .flatMap((report, m) =>
        (Array.isArray(report.reports) ? report.reports : [])
          .filter((send) => !!send)
          .map((send, s): SendRow => {
            const at = toDate(send.messageSchedules ?? null);
            const status = String(send.scheduleStatus ?? '');
            const rate = Number(send.deliveryRate);
            // The API reports 0% for sends that have not produced receipts yet.
            const settled = /complet|finish|stop|fail|abandon|cancel/i.test(status);
            return {
              key: `${report.referenceId ?? m}-${send.scheduleUuid ?? s}`,
              referenceId: String(report.referenceId ?? ''),
              message: String(report.message ?? '').trim() || 'Untitled message',
              when: at ? formatWhen(at) : '—',
              at: at?.getTime() ?? 0,
              status,
              recipients: send.totalRecipient ? formatCount(send.totalRecipient) : '—',
              rate: Number.isFinite(rate) && (rate > 0 || settled) ? Math.max(0, Math.min(100, rate)) : null,
            };
          })
          .sort((a, b) => a.at - b.at)
      );
  }

  openReport(row: SendRow) {
    if (!row.referenceId) return;
    // Same target as before: <messageId>/message-report under the reports route.
    this.router.navigate([row.referenceId, 'message-report'], { relativeTo: this.route });
  }
}
